import { useEffect, useLayoutEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, Text } from "@nbs/ui";
import { useOnboarding } from "./OnboardingProvider";

type Rect = { top: number; left: number; width: number; height: number };

function readTargetRect(target: string): Rect | null {
  const el = document.querySelector(`[data-tour="${target}"]`);
  if (!(el instanceof HTMLElement)) return null;
  const r = el.getBoundingClientRect();
  if (r.width < 2 && r.height < 2) return null;
  const pad = 8;
  return {
    top: Math.max(8, r.top - pad),
    left: Math.max(8, r.left - pad),
    width: Math.min(window.innerWidth - 16, r.width + pad * 2),
    height: Math.min(window.innerHeight - 16, r.height + pad * 2),
  };
}

function routeParts(route: string): { pathname: string; search: string } {
  const [pathname = "/", search = ""] = route.split("?");
  return { pathname, search: search ? `?${search}` : "" };
}

/**
 * Non-dismissible guided tour: no close button, backdrop clicks ignored.
 * Advance only via Précédent / Suivant / Terminer.
 */
export function OnboardingTour() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const tour = useOnboarding();
  const [rect, setRect] = useState<Rect | null>(null);
  const [popoverPos, setPopoverPos] = useState<{ top: number; left: number }>({
    top: 80,
    left: 80,
  });

  const step = tour.step;

  useEffect(() => {
    if (!tour.active || !step?.route) return;
    const wanted = routeParts(step.route);
    const herePath = location.pathname;
    const hereSearch = location.search;
    if (herePath !== wanted.pathname || hereSearch !== wanted.search) {
      void navigate(`${wanted.pathname}${wanted.search}`);
    }
  }, [tour.active, location.pathname, location.search, navigate, step?.id, step?.route]);

  useLayoutEffect(() => {
    if (!tour.active || !step) {
      setRect(null);
      return;
    }

    let cancelled = false;
    let tries = 0;

    const measure = () => {
      if (cancelled) return;
      const next = readTargetRect(step.target);
      if (!next && tries < 14) {
        tries += 1;
        window.setTimeout(measure, 90);
        return;
      }
      const fallback: Rect = {
        top: Math.max(24, window.innerHeight / 2 - 48),
        left: Math.max(24, window.innerWidth / 2 - 160),
        width: 320,
        height: 96,
      };
      const box = next ?? fallback;
      setRect(box);

      const popW = 340;
      const popH = 240;
      let top = box.top + box.height + 14;
      let left = box.left;
      if (top + popH > window.innerHeight - 16) {
        top = Math.max(16, box.top - popH - 14);
      }
      if (left + popW > window.innerWidth - 16) {
        left = Math.max(16, window.innerWidth - popW - 16);
      }
      setPopoverPos({ top, left });

      const el = document.querySelector(`[data-tour="${step.target}"]`);
      if (el instanceof HTMLElement) {
        el.scrollIntoView({ block: "nearest", inline: "nearest", behavior: "smooth" });
      }
    };

    measure();
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => {
      cancelled = true;
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [tour.active, step]);

  if (!tour.active || !step) return null;

  return (
    <div className="nbs-tour" data-testid="onboarding-tour" aria-live="polite">
      <div className="nbs-tour__scrim" aria-hidden="true" />
      {rect ? (
        <div
          className="nbs-tour__spotlight"
          aria-hidden="true"
          style={{
            top: rect.top,
            left: rect.left,
            width: rect.width,
            height: rect.height,
          }}
        />
      ) : null}

      <div
        className="nbs-tour__popover"
        role="dialog"
        aria-modal="true"
        aria-labelledby="nbs-tour-title"
        style={{ top: popoverPos.top, left: popoverPos.left }}
      >
        <p className="nbs-tour__mission">{t(tour.objectiveKey)}</p>
        <p className="nbs-tour__progress">
          {t("onboarding.progress", {
            current: tour.stepIndex + 1,
            total: tour.steps.length,
          })}
        </p>
        <h2 id="nbs-tour-title" className="nbs-tour__title">
          {t(step.titleKey)}
        </h2>
        <Text tone="muted" className="nbs-tour__body">
          {t(step.bodyKey)}
        </Text>
        <div className="nbs-tour__actions">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={tour.isFirst}
            onClick={tour.prev}
          >
            {t("onboarding.prev")}
          </Button>
          {tour.isLast ? (
            <Button type="button" variant="primary" size="sm" onClick={tour.finish}>
              {t("onboarding.finish")}
            </Button>
          ) : (
            <Button type="button" variant="primary" size="sm" onClick={tour.next}>
              {t("onboarding.next")}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
